/**
 * App Debug Logger Utility
 * 
 * Provides debug logging for the entire app (components, composables, services, etc.)
 * Logs are gated behind settingsStore.console flag when available.
 * When Pinia isn't ready (early init), falls back to plain console with same formatting.
 * 
 * Usage:
 *   import { appDebugLog } from '@/utils/appDebugLog';
 *   appDebugLog.info('MyComponent', 'Initialized', { count: 5 });
 *   appDebugLog.warn('MyComponent', 'Unknown provider', { slug: 'unknown' });
 *   appDebugLog.error('MyComponent', 'Failed to load', error);
 *   appDebugLog.debug('MyComponent', 'Debug info', data);
 *   appDebugLog.success('MyComponent', 'Operation completed', result);
 *   appDebugLog.fatal('MyComponent', 'Critical error', error);
 *   appDebugLog.log('MyComponent', 'General log', data);
 */

import { useSettingsStore } from '@/stores';

// Color styles matching Logger.js
const styles = {
  infoBg: 'background-color: rgba(37, 99, 235, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  infoTxt: 'color: rgba(37, 99, 235, 1); padding: 3px 4px; border-radius: 0.175rem',
  debugBg: 'background-color: rgba(156, 163, 175, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  debugTxt: 'color: rgba(156, 163, 175, 1); padding: 3px 4px; border-radius: 0.175rem',
  warnBg: 'background-color: rgba(217, 119, 6, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  warnTxt: 'color: rgba(217, 119, 6, 1); padding: 3px 4px; border-radius: 0.175rem',
  errorBg: 'background-color: rgba(220, 38, 38, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  errorTxt: 'color: rgba(220, 38, 38, 1); padding: 3px 4px; border-radius: 0.175rem',
  fatalBg: 'background-color: rgba(0, 0, 0, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  fatalTxt: 'color: rgba(220, 38, 38, 1); padding: 3px 4px; border-radius: 0.175rem',
  successBg: 'background-color: rgba(5, 150, 105, 1); color: white; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  successTxt: 'color: rgba(16, 185, 129, 1); padding: 3px 4px; border-radius: 0.175rem',
  logBg: 'background-color: rgba(235, 235, 235, 1); color: #ccc; padding: 3px 3px 3px 4px; border-radius: 0.175rem',
  logTxt: 'color: #444; padding: 3px 4px; border-radius: 0.175rem'
};

/**
 * Check if logging is enabled
 * Returns: { enabled: boolean, piniaReady: boolean }
 * When Pinia isn't ready, we'll still log but without gating
 */
function getLoggingState () {
  try {
    const settingsStore = useSettingsStore();
    return { enabled: settingsStore.console, piniaReady: true };
  }
  catch {
    // Pinia not yet initialized - allow logging anyway (early init)
    return { enabled: true, piniaReady: false };
  }
}

/**
 * Gated debug logging for app-wide use (with colors)
 * All methods match Logger.js: debug, info, warn, error, fatal, success, log
 * 
 * When Pinia isn't ready (early app init), logs are NOT gated - they always appear.
 * This ensures DB migration logs and early startup logs are visible.
 */
export const appDebugLog = {
  /**
   * Log debug message (gray - for verbose debugging)
   */
  debug (context, message, data = undefined) {
    const { enabled } = getLoggingState();
    if (!enabled) return;
    console.debug(`%c[debug]%c${context}: ${message}`, styles.debugBg, styles.debugTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log info message (blue - general information)
   */
  info (context, message, data = undefined) {
    const { enabled } = getLoggingState();
    if (!enabled) return;
    console.info(`%c[info]%c${context}: ${message}`, styles.infoBg, styles.infoTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log warning message (orange - potential issues)
   */
  warn (context, message, data = undefined) {
    const { enabled } = getLoggingState();
    if (!enabled) return;
    console.warn(`%c[warn]%c${context}: ${message}`, styles.warnBg, styles.warnTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log error message (red - always logs regardless of settings)
   */
  error (context, message, data = undefined) {
    // Errors always log, regardless of settings
    console.error(`%c[error]%c${context}: ${message}`, styles.errorBg, styles.errorTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log fatal error message (black background - critical errors, always logs)
   */
  fatal (context, message, data = undefined) {
    // Fatal errors always log, regardless of settings
    console.error(`%c[fatal]%c${context}: ${message}`, styles.fatalBg, styles.fatalTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log success message (green - successful operations)
   */
  success (context, message, data = undefined) {
    const { enabled } = getLoggingState();
    if (!enabled) return;
    console.log(`%c[success]%c${context}: ${message}`, styles.successBg, styles.successTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  },

  /**
   * Log general message (gray background - default logging)
   */
  log (context, message, data = undefined) {
    const { enabled } = getLoggingState();
    if (!enabled) return;
    console.log(`%c[log]%c${context}: ${message}`, styles.logBg, styles.logTxt);
    if (data !== undefined) {
      console.log('\t\t>', data);
    }
  }
};

export default appDebugLog;
